import { motion } from "framer-motion";
import { Reveal, SectionHeading } from "./Reveal";
import { ShieldAlert, Building2, Factory, ArrowRight, Flag } from "lucide-react";
import { scrollToId } from "./Header";
import { useLang } from "@/i18n/LanguageContext";

const THREAT_K = ["3 yrs", "70%", "HNDL + TNFL"];
const TIER_ICONS = [ShieldAlert, Building2, Factory];
const TIER_CLS = ["border-amber-300 bg-amber-50/60", "border-blue-200 bg-blue-50/50", "border-emerald-200 bg-emerald-50/60"];
const TIER_CHIPS = [
  ["Defence", "Power", "Telecom", "ISRO", "DRDO", "ONGC", "Banking Core"],
  ["Government", "Financial Services", "Healthcare", "Insurance", "IT Services"],
  ["HSM Vendors", "Cloud Providers", "PKI Operators", "Network OEMs"],
];
const STAT_K = ["₹6,003 Cr", "4 Hubs", "152", "3+1"];
const CII_YEARS = ["2027", "2028", "2029"];
const ENT_YEARS = ["2028", "2030", "2033"];

export const Roadmap = () => {
  const { t } = useLang();

  const TRACKS = [
    {
      id: "cii",
      years: CII_YEARS,
      data: t("roadmap.cii"),
      span: "2027 → 2029",
      chipCls: "text-amber-700 bg-amber-50 border-amber-300",
      dotCls: "border-amber-500 text-amber-600 shadow-[0_0_0_6px_rgba(217,119,6,0.1)]",
      barCls: "from-amber-500 to-amber-300",
      bulletCls: "bg-amber-500",
    },
    {
      id: "enterprise",
      years: ENT_YEARS,
      data: t("roadmap.ent"),
      span: "2028 → 2033",
      chipCls: "text-blue-700 bg-blue-50 border-blue-300",
      dotCls: "border-blue-600 text-blue-700 shadow-[0_0_0_6px_rgba(29,78,216,0.1)]",
      barCls: "from-blue-600 to-blue-300",
      bulletCls: "bg-blue-600",
    },
  ];

  return (
    <section id="roadmap" data-testid="roadmap-section" className="py-20 lg:py-28 bg-[#F8FAFC] bg-blueprint relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow={t("roadmap.eyebrow")}
          title={
            <>
              {t("roadmap.titleA")}
              <span className="text-blue-700">{t("roadmap.titleB")}</span>
            </>
          }
          sub={t("roadmap.sub")}
        />

        <Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-slate-200/90 border border-slate-200 rounded-2xl overflow-hidden mb-16" data-testid="roadmap-threat-strip">
            {THREAT_K.map((k, i) => (
              <div key={k} className="bg-white px-6 py-6">
                <p className="font-mono text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">{k}</p>
                <p className="mt-2 text-xs sm:text-sm text-slate-500 leading-relaxed">{t("roadmap.threats")[i]}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <div className="space-y-14">
          {TRACKS.map((track, ti) => (
            <div key={track.id} data-testid={`roadmap-track-${track.id}`}>
              <Reveal>
                <div className="flex flex-wrap items-center gap-3 mb-7">
                  <span className={`text-[11px] font-mono font-bold uppercase tracking-[0.2em] border px-3.5 py-1.5 rounded-full ${track.chipCls}`}>
                    {track.data.label}
                  </span>
                  <span className="font-mono text-sm font-bold text-slate-400">{track.span}</span>
                  <span className="text-sm text-slate-500 w-full sm:w-auto">{track.data.desc}</span>
                </div>
              </Reveal>
              <div className="relative">
                <div className={`hidden lg:block absolute top-[26px] left-[6%] right-[6%] h-1 rounded-full bg-gradient-to-r ${track.barCls} opacity-40`} />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {track.data.milestones.map((m, i) => (
                    <motion.div
                      key={track.years[i]}
                      initial={{ opacity: 0, y: 36 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ duration: 0.7, delay: ti * 0.1 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <div className="relative z-10 flex lg:justify-center mb-5">
                        <span className={`shrink-0 grid place-items-center w-[52px] h-[52px] rounded-full bg-white border-2 font-mono font-extrabold text-sm ${track.dotCls}`}>
                          '{track.years[i].slice(2)}
                        </span>
                      </div>
                      <div className="h-full p-6 rounded-2xl border border-slate-200/90 bg-white hover:shadow-[0_18px_44px_-18px_rgba(15,23,42,0.22)] hover:-translate-y-1 transition-all duration-400">
                        <p className="font-mono text-[11px] font-bold tracking-[0.18em] text-slate-400">{track.data.label.toUpperCase()} · {track.years[i]}</p>
                        <h3 className="mt-2 text-lg font-bold text-slate-900 tracking-tight">{m.title}</h3>
                        <ul className="mt-3.5 space-y-2">
                          {m.points.map((p) => (
                            <li key={p} className="flex items-start gap-2.5 text-sm text-slate-500 leading-relaxed">
                              <span className={`mt-2 w-1.5 h-1.5 rounded-full shrink-0 ${track.bulletCls}`} />
                              {p}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-20">
          <Reveal>
            <h3 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight mb-8">
              {t("roadmap.tiersTitle")}
            </h3>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {t("roadmap.tiers").map((tier, i) => {
              const Icon = TIER_ICONS[i];
              return (
                <Reveal key={tier.title} delay={i * 0.1}>
                  <div data-testid={`roadmap-tier-${i + 1}`} className={`h-full p-6 rounded-2xl border ${TIER_CLS[i]}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[11px] font-bold tracking-[0.2em] uppercase text-slate-500">Tier {i + 1}</span>
                      <Icon className="w-5 h-5 text-slate-700" />
                    </div>
                    <h4 className="mt-3 text-lg font-bold text-slate-900 tracking-tight">{tier.title}</h4>
                    <p className="mt-2 text-sm text-slate-500 leading-relaxed">{tier.desc}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {TIER_CHIPS[i].map((c) => (
                        <span key={c} className="text-[10px] font-mono font-bold tracking-[0.12em] text-slate-600 bg-white border border-slate-200 px-2.5 py-1 rounded-full">
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-16 p-7 sm:p-9 rounded-3xl border border-blue-200 bg-gradient-to-br from-blue-50/80 via-white to-white flex flex-col lg:flex-row items-start lg:items-center gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-2.5">
                <Flag className="w-5 h-5 text-blue-700" />
                <p className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t("roadmap.fitLabel")}</p>
              </div>
              <p className="mt-3 text-base text-slate-600 leading-relaxed max-w-2xl">{t("roadmap.fitBody")}</p>
              <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2">
                {STAT_K.map((k, i) => (
                  <div key={k}>
                    <p className="font-mono font-extrabold text-slate-900">{k}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">{t("roadmap.statsV")[i]}</p>
                  </div>
                ))}
              </div>
            </div>
            <button
              data-testid="roadmap-cta-button"
              onClick={() => scrollToId("#contact")}
              className="group shrink-0 inline-flex items-center gap-2 text-sm font-bold text-white bg-blue-700 hover:bg-slate-900 px-7 py-4 rounded-full shadow-[0_14px_32px_-12px_rgba(29,78,216,0.55)] transition-all duration-300 hover:-translate-y-0.5"
            >
              {t("roadmap.fitCta")}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </Reveal>

        <p className="mt-8 text-[11px] text-slate-400 leading-relaxed max-w-3xl">{t("roadmap.disclaimer")}</p>
      </div>
    </section>
  );
};
